import { createElement, FunctionComponent } from 'react';
import classNames from 'classnames';

import {
  DefaultSizeIconsContent,
  MediumSizeIconsContent,
  SmallSizeIconsContent,
} from '../../constants/IconsContent';
import { IconContentFunction, type IconName, IconSize } from '../../types/Icon';

import styles from './Icon.module.scss';

export { IconSize, IconName };

export type IconProps = {
  icon: IconName;
  size?: IconSize | number;
  tagName?: keyof JSX.IntrinsicElements;
  className?: string;
  autoScale?: boolean;
  title?: string;
  'data-test'?: string;
};

export const Icon: FunctionComponent<IconProps> = ({
  icon,
  size = IconSize.DEFAULT,
  tagName = 'span',
  className,
  autoScale = true,
  title,
  ...props
}) => {
  return createElement(
    tagName,
    {
      'className': classNames(styles.container, className),
      title,
      'data-test': props['data-test'],
    },
    createIconContent(icon, size, autoScale),
  );
};

function createIconContent(
  icon: IconName,
  size: number,
  autoScale: boolean,
): JSX.Element | null {
  const IconContent = autoScale
    ? getBestSizeFitContentFunction(icon, size)
    : getExactSizeContentFunction(icon, size);

  return IconContent && (
    createElement(IconContent, { size, color: 'currentColor' })
  );
}

function getExactSizeContentFunction(icon: IconName, size: IconSize): IconContentFunction | null {
  let collection;

  switch (size) {
    case IconSize.SMALL:
      collection = SmallSizeIconsContent;
      break;

    case IconSize.MEDIUM:
      collection = MediumSizeIconsContent;
      break;

    case IconSize.DEFAULT:
      collection = DefaultSizeIconsContent;
      break;

    default:
      throw new Error(`unrecognized exact size ${size}`);
  }

  return collection[icon];
}

const CLOSER_TO_SMALL = IconSize.SMALL + ((IconSize.MEDIUM - IconSize.SMALL) / 2);
const CLOSER_TO_DEFAULT = IconSize.MEDIUM + ((IconSize.DEFAULT - IconSize.MEDIUM) / 2);

function getBestSizeFitContentFunction(icon: IconName, size: IconSize): IconContentFunction | null {
  let sizedCollectionsByPriority;

  if (size < CLOSER_TO_SMALL) {
    sizedCollectionsByPriority = [
      SmallSizeIconsContent,
      MediumSizeIconsContent,
      DefaultSizeIconsContent,
    ];
  }
  else if (size > CLOSER_TO_DEFAULT) {
    sizedCollectionsByPriority = [
      DefaultSizeIconsContent,
      MediumSizeIconsContent,
      SmallSizeIconsContent,
    ];
  }
  else if (size <= IconSize.MEDIUM) {
    sizedCollectionsByPriority = [
      MediumSizeIconsContent,
      SmallSizeIconsContent,
      DefaultSizeIconsContent,
    ];
  }
  else if (size > IconSize.MEDIUM) {
    sizedCollectionsByPriority = [
      MediumSizeIconsContent,
      DefaultSizeIconsContent,
      SmallSizeIconsContent,
    ];
  }
  else {
    console.error('Unexpected icon size:', size);
    return null;
  }

  for (const map of sizedCollectionsByPriority) {
    if (map[icon]) {
      return map[icon];
    }
  }

  return null; // nothing found
}
